import { FormGroup, Icon, InputGroup, MenuItem } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Select } from "@blueprintjs/select";
import React, { useEffect, useRef } from "react";
import { useOverflowTooltip } from "../../../../utils/field";
import { RelativityTooltip } from "../../../Tooltip/RelativityTooltip";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import {
  Option,
  EnumSingleSelectFieldValue,
  FieldLabeledProps,
} from "../../types/fieldTypes";
import { EnumSingleSelectProps } from "../../types/fieldTypes";

/**Interface for EnumSingleSelectField*/
export interface EnumSingleSelectFieldProps extends EnumSingleSelectProps {
  readonly type: "EnumSingleSelect";
  reorderOptions?: ReorderOption[];
  noResultsLabel?: string;
  labelProps?: FieldLabeledProps;
}

/** Used for reordering OptionValues of the dropdown*/
export interface ReorderOption {
  newIndex: number;
  value: string | number;
}

/**Single Select is used primarily with enum values from the API, but can be passed collection of arrays*/
export const EnumSingleSelectField: React.FC<EnumSingleSelectFieldProps> = (
  props
) => {
  const [localValue, setLocalValue] =
    React.useState<EnumSingleSelectFieldValue>(props.value);
  const selectInputRef = useRef<HTMLInputElement | null>(null);
  const isTooltipVisible = useOverflowTooltip(
    selectInputRef.current,
    props.value
  );

  const getSelectValue = (
    selectValue: EnumSingleSelectFieldValue,
    options: Option<EnumSingleSelectFieldValue>[]
  ) => {
    return (options || []).find(
      (option) => String(option.value) === String(selectValue)
    );
  };

  const selectedOption = getSelectValue(localValue, props.optionValues);

  const orderedOptionValues = () => {
    if (!!props.reorderOptions) {
      props.reorderOptions.forEach((element) => {
        const foundOptionIndex = (props.optionValues || []).findIndex(
          (x) => x.value === element.value
        );

        props.optionValues.splice(
          element.newIndex,
          0,
          props.optionValues.splice(foundOptionIndex, 1)[0]
        );
      });
    }
    return props.optionValues;
  };

  const selectedOptionToUse = selectedOption
    ? (selectedOption as Option<EnumSingleSelectFieldValue>)?.label
    : "";

  const checkIfSelectedValueIsValid = (
    onChangeEvent: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isValidOption = getSelectValue(
      onChangeEvent.target.value,
      props.optionValues
    );
    if (!isValidOption) {
      onChangeEvent.target.value = "";
    }
  };

  useEffect(() => {
    setLocalValue(props.value);
  }, [props.value]);

  return (
    <FormGroup
      key={localValue}
      className={`${
        props.isRequired && !localValue
          ? "has-required-background"
          : "select-field"
      }`}
    >
      {props.label && (
        <FieldLabel
          labelName={props.label}
          tooltipDescription={props.description}
          fieldProps={{ ...props, value: localValue }}
          isRequired={props.isRequired}
          {...props.labelProps}
        />
      )}
      <Select<Option<EnumSingleSelectFieldValue>>
        items={orderedOptionValues()}
        noResults={props.noResultsLabel ? props.noResultsLabel : "No Results"}
        resetOnSelect={true}
        filterable={(props.optionValues || []).length >= 10}
        popoverProps={{
          position: "bottom-left",
          minimal: true,
          hasBackdrop: true,
          canEscapeKeyClose: true,
        }}
        itemListPredicate={(query, items) => {
          query = query.toLowerCase();

          return (items || []).filter((optionValue) =>
            optionValue.label.toLowerCase().includes(query)
          );
        }}
        itemRenderer={(item, itemProps) => {
          return (
            <MenuItem
              className="select__menu-item"
              key={itemProps.index}
              text={item.label}
              active={String(localValue) === String(item.value)}
              onClick={itemProps.handleClick}
            />
          );
        }}
        onItemSelect={(item) => {
          setLocalValue(item.value as EnumSingleSelectFieldValue);
          props.onSubmit && props.onSubmit(item.value);
        }}
        disabled={props.disabled}
      >
        <RelativityTooltip
          shouldShowTooltip={isTooltipVisible}
          content={selectedOptionToUse}
        >
          <InputGroup
            inputRef={selectInputRef}
            className="select-input"
            rightElement={
              <Icon className="select-right-icon" icon={IconNames.CaretDown} />
            }
            readOnly={true}
            disabled={props.disabled}
            placeholder={props.placeholder}
            onChange={(e) => {
              checkIfSelectedValueIsValid(e);
            }}
            value={selectedOptionToUse}
          />
        </RelativityTooltip>
      </Select>
      {props.errorMessages && (
        <FormFieldErrorContainer errorMessages={props.errorMessages} />
      )}
    </FormGroup>
  );
};
